// Modern replacement for react-scroll-sync using React.createContext instead of
// the deprecated childContextTypes/contextTypes legacy context API.
import React, { createContext, useContext, useRef, useCallback } from 'react';

interface ScrollSyncContextValue {
  registerPane: (node: HTMLElement, groups: string[]) => void;
  unregisterPane: (node: HTMLElement, groups: string[]) => void;
}

const ScrollSyncContext = createContext<ScrollSyncContextValue | null>(null);

export function ScrollSync({
  children,
  proportional = true,
  vertical = true,
  horizontal = true,
  enabled = true,
}: {
  children: React.ReactNode;
  proportional?: boolean;
  vertical?: boolean;
  horizontal?: boolean;
  enabled?: boolean;
}) {
  const panesRef = useRef<Record<string, HTMLElement[]>>({});
  const propsRef = useRef({ proportional, vertical, horizontal, enabled });
  propsRef.current = { proportional, vertical, horizontal, enabled };

  const syncScrollPosition = (scrolledPane: HTMLElement, pane: HTMLElement) => {
    const { proportional: p, vertical: v, horizontal: h } = propsRef.current;
    const {
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = scrolledPane;
    const scrollTopOffset = scrollHeight - clientHeight;
    const scrollLeftOffset = scrollWidth - clientWidth;
    const paneHeight = pane.scrollHeight - clientHeight;
    const paneWidth = pane.scrollWidth - clientWidth;
    if (v && scrollTopOffset > 0) {
      pane.scrollTop = p
        ? (paneHeight * scrollTop) / scrollTopOffset
        : scrollTop;
    }
    if (h && scrollLeftOffset > 0) {
      pane.scrollLeft = p
        ? (paneWidth * scrollLeft) / scrollLeftOffset
        : scrollLeft;
    }
  };

  const addEvents = (node: HTMLElement, groups: string[]) => {
    node.onscroll = () => {
      if (!propsRef.current.enabled) return;
      window.requestAnimationFrame(() => {
        groups.forEach((group: string) => {
          (panesRef.current[group] || []).forEach((pane: HTMLElement) => {
            if (pane !== node) {
              pane.onscroll = null;
              syncScrollPosition(node, pane);
              window.requestAnimationFrame(() => addEvents(pane, groups));
            }
          });
        });
      });
    };
  };

  const registerPane = useCallback((node: HTMLElement, groups: string[]) => {
    groups.forEach((group: string) => {
      if (!panesRef.current[group]) panesRef.current[group] = [];
      if (!panesRef.current[group].includes(node)) {
        if (panesRef.current[group].length > 0) {
          syncScrollPosition(panesRef.current[group][0], node);
        }
        panesRef.current[group].push(node);
      }
    });
    addEvents(node, groups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unregisterPane = useCallback((node: HTMLElement, groups: string[]) => {
    groups.forEach((group: string) => {
      const arr = panesRef.current[group] || [];
      const idx = arr.indexOf(node);
      if (idx >= 0) {
        node.onscroll = null;
        arr.splice(idx, 1);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScrollSyncContext.Provider value={{ registerPane, unregisterPane }}>
      {React.Children.only(children)}
    </ScrollSyncContext.Provider>
  );
}

export function ScrollSyncPane({
  children,
  group = 'default',
  enabled = true,
}: {
  children: React.ReactNode;
  group?: string | string[];
  enabled?: boolean;
}) {
  const context = useContext(ScrollSyncContext);
  const nodeRef = useRef<HTMLElement | null>(null);
  const groups = ([] as string[]).concat(group);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (nodeRef.current && context) {
        context.unregisterPane(nodeRef.current, groups);
      }
      nodeRef.current = node;
      if (node && enabled && context) {
        context.registerPane(node, groups);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return React.cloneElement(
    React.Children.only(children) as React.ReactElement,
    { ref },
  );
}
